import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import {
  API,
  FORECASTLY_LOGO_LIGHT,
  ONBOARDING_DRAFT_KEY,
} from "@/lib/brand";
import {
  ArrowRight,
  ArrowLeft,
  House,
} from "@phosphor-icons/react";
import StepVideo from "@/components/onboarding/StepVideo";
import NewMonthDialog from "@/components/onboarding/NewMonthDialog";
import { rollForward, monthLabel } from "@/lib/onboardingRollover";
import { BusinessStep, OpeningCashStep, PayingYourselfStep } from "@/components/onboarding/BasicsSteps";
import { RegularCostsStep, OneOffCostsStep, ContingencyDialog } from "@/components/onboarding/CostSteps";
import { contingencyAmount, LONG } from "@/lib/costHelpers";
import { IncomeStep, NoIncomeDialog } from "@/components/onboarding/IncomeStep";
import RhythmStep from "@/components/onboarding/RhythmStep";
import GoalsStep from "@/components/onboarding/GoalsStep";
import NearlyThereStep from "@/components/onboarding/NearlyThere";
import WelcomeStep from "@/components/onboarding/WelcomeStep";
import { SEASONALITY_PRESETS, followsRhythm, launchMonthIndex } from "@/lib/incomeHelpers";

// "Build your forecast" — 10 steps (Welcome is Step 1). Costs come before income.
// Progress is saved to the account (onboarding_drafts) so it works across devices.

const todayStartMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const STEPS = [
  { key: "welcome", title: "Welcome", subtitle: "", intro: true },
  { key: "business", title: "Your business basics", subtitle: "" },
  { key: "opening_cash", title: "Opening cash", subtitle: "This is your starting point." },
  { key: "recurring_costs", title: "Regular costs", subtitle: "Everything that repeats." },
  { key: "one_off", title: "One-off costs", subtitle: "Things you'll pay for just once in the next 12 months." },
  { key: "personal_draw", title: "Paying yourself", subtitle: "You matter too — and your business should support you." },
  { key: "revenue", title: "Your income streams", subtitle: "What you sell, and anything else that brings money in." },
  { key: "seasonality", title: "Your rhythm", subtitle: "Optional — pick what feels closest." },
  { key: "goals", title: "Your 12-month plans", subtitle: "Goals, launches, targets." },
  { key: "nearly_there", title: "Nearly there…", subtitle: "" },
];

const stepIdxOf = (key) => STEPS.findIndex((s) => s.key === key);
const DETOUR_LABELS = { nearly_there: "your review" };
const detourLabel = (key) => DETOUR_LABELS[key] || STEPS[stepIdxOf(key)].title;
const LEGACY_STEP_KEYS = [
  "welcome", "business", "opening_cash", "bank_upload", "revenue", "seasonality",
  "recurring_costs", "one_off", "personal_draw", "goals", "nearly_there",
];

const emptyOffer = () => ({
  id: `rs${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
  name: "",
  price: 0,
  clients_per_month: 0,
  is_future: false,
  launch_month_index: 0,
  offer_type: "service",
  monthly_sales: null,
});

const emptyCost = (kind) => ({
  id: `c${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
  name: "",
  amount: 0,
  kind,
  month_index: kind === "one_off" ? 0 : null,
  frequency: kind === "one_off" ? null : "monthly",
  anchor_month_index: 0,
  months_active: null,
});

const emptyGoal = () => ({
  id: `g${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
  title: "",
  why: "",
  target_value: "",
  target_month_index: "",
  kind: "revenue",
});

const PROFILE_KEYS = ["industry", "employees", "legal_entity", "years_trading", "biggest_challenge", "vat_registered"];
const profileFromUser = (user) =>
  Object.fromEntries(PROFILE_KEYS.map((k) => [k, user?.business_profile?.[k] || ""]));

const initialInputs = (user) => ({
  business_name: user?.business_name || "",
  start_month: todayStartMonth(),
  business_profile: profileFromUser(user),
  contingency_asked: false,
  opening_cash: 0,
  revenue_streams: [emptyOffer()],
  recurring_costs: [emptyCost("monthly")],
  one_off_costs: [emptyCost("one_off")],
  goals: [],
  personal_draw: 0,
  seasonality_preset: "steady",
  seasonality_note: "",
});

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();

  const [loaded, setLoaded] = useState(false);
  const [idx, setIdx] = useState(0);
  const [inputs, setInputs] = useState(() => initialInputs(user));
  const [busy, setBusy] = useState(false);
  const [detour, setDetour] = useState(null);
  const [newMonth, setNewMonth] = useState(null);
  const [videos, setVideos] = useState({});
  const finishedRef = useRef(false);
  const profilePushed = useRef(null);
  const latestDraft = useRef(null);

  const step = STEPS[idx];
  const progress = Math.round(((idx + 1) / STEPS.length) * 100);

  const goNext = () => setIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const goBack = () => setIdx((i) => Math.max(0, i - 1));
  const goToKey = (key) => setIdx(Math.max(0, stepIdxOf(key)));
  // Detour: jump elsewhere; the main button then offers "Back to …" the step we came from.
  const startDetour = (targetKey, returnKey = step.key) => {
    setDetour({ returnKey });
    goToKey(targetKey);
  };
  const endDetour = () => {
    const k = detour.returnKey;
    setDetour(null);
    goToKey(k);
  };
  const jumpTo = (key) => startDetour(key, "nearly_there");

  useEffect(() => {
    if (detour && step.key === detour.returnKey) setDetour(null);
  }, [step.key, detour]);

  // Mandatory-field gating for Screen 2 (business basics).
  const nextDisabled =
    step.key === "business" &&
    (!(inputs.business_name || "").trim() ||
      !(inputs.business_profile.legal_entity || "").trim() ||
      !inputs.business_profile.vat_registered);

  // Load: account draft → legacy browser draft → fresh start. Roll forward if a month has passed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let draft = null;
      try {
        draft = (await axios.get(`${API}/onboarding/draft`)).data.draft;
      } catch {}
      if (!draft) {
        try {
          const p = JSON.parse(localStorage.getItem(ONBOARDING_DRAFT_KEY) || "null");
          if (p && p.userEmail === user?.email && p.inputs) {
            draft = { step_key: LEGACY_STEP_KEYS[p.idx] || "welcome", inputs: p.inputs, forecast_month: p.inputs.start_month };
          }
        } catch {}
      }
      try {
        localStorage.removeItem(ONBOARDING_DRAFT_KEY);
      } catch {}
      if (cancelled) return;
      if (draft?.inputs) {
        const base = initialInputs(user);
        let restored = {
          ...base,
          ...draft.inputs,
          business_profile: { ...(draft.inputs.business_profile || {}) },
        };
        PROFILE_KEYS.forEach((k) => {
          if (base.business_profile[k]) restored.business_profile[k] = base.business_profile[k];
          else if (restored.business_profile[k] == null) restored.business_profile[k] = "";
        });
        if (user?.business_name) restored.business_name = user.business_name;
        delete restored.bank_upload;
        let key = draft.step_key === "bank_upload" ? "recurring_costs" : draft.step_key;
        if (stepIdxOf(key) < 0) key = "welcome";
        const today = todayStartMonth();
        const from = draft.forecast_month || restored.start_month || today;
        if (from < today) {
          const rolled = rollForward(restored, from, today);
          restored = rolled.inputs;
          if (stepIdxOf(key) > stepIdxOf("opening_cash")) setNewMonth({ dropped: rolled.dropped, resumeKey: key });
        }
        restored.start_month = today;
        setInputs(restored);
        setIdx(stepIdxOf(key));
      }
      setLoaded(true);
    })();
    axios.get(`${API}/onboarding/videos`).then((r) => setVideos(r.data.videos || {})).catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  latestDraft.current = loaded
    ? { idx, step_key: step.key, inputs, forecast_month: inputs.start_month || todayStartMonth() }
    : null;
  const saveDraft = () =>
    !finishedRef.current && latestDraft.current
      ? axios.put(`${API}/onboarding/draft`, latestDraft.current).catch(() => {})
      : Promise.resolve();

  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => {
      saveDraft();
      const prof = JSON.stringify({ business_name: inputs.business_name, business_profile: inputs.business_profile });
      if (profilePushed.current === null) profilePushed.current = prof;
      else if (prof !== profilePushed.current) {
        profilePushed.current = prof;
        axios.patch(`${API}/auth/profile`, JSON.parse(prof)).then(() => refreshUser && refreshUser()).catch(() => {});
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, idx, inputs]);

  useEffect(() => {
    if (loaded) saveDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  useEffect(() => {
    const flush = () => {
      if (finishedRef.current || !latestDraft.current) return;
      fetch(`${API}/onboarding/draft`, {
        method: "PUT",
        keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: axios.defaults.headers.common["Authorization"] || "" },
        body: JSON.stringify(latestDraft.current),
      }).catch(() => {});
    };
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, []);

  // ====== Mutators ======
  const patchBiz = (patch) =>
    setInputs((s) => ({ ...s, business_profile: { ...s.business_profile, ...patch } }));
  const updateOffer = (i, patch) =>
    setInputs((s) => ({
      ...s,
      revenue_streams: s.revenue_streams.map((r, k) => (k === i ? { ...r, ...patch } : r)),
    }));
  const addOffer = () => setInputs((s) => ({ ...s, revenue_streams: [...s.revenue_streams, emptyOffer()] }));
  const removeOffer = (i) =>
    setInputs((s) => ({ ...s, revenue_streams: s.revenue_streams.filter((_, k) => k !== i) }));

  const updateGoal = (i, patch) =>
    setInputs((s) => ({ ...s, goals: s.goals.map((g, k) => (k === i ? { ...g, ...patch } : g)) }));
  const addGoal = () => setInputs((s) => ({ ...s, goals: [...s.goals, emptyGoal()] }));
  const removeGoal = (i) => setInputs((s) => ({ ...s, goals: s.goals.filter((_, k) => k !== i) }));

  const addCost = (kind, item = emptyCost(kind)) =>
    setInputs((s) => ({
      ...s,
      [kind === "monthly" ? "recurring_costs" : "one_off_costs"]: [
        ...s[kind === "monthly" ? "recurring_costs" : "one_off_costs"],
        item,
      ],
    }));
  const [contingency, setContingency] = useState(null);
  const [noIncome, setNoIncome] = useState(false);
  const [focusCardId, setFocusCardId] = useState(null);
  const handleNext = () => {
    if (step.key === "recurring_costs" && !inputs.contingency_asked) return setContingency({ fromNext: true });
    if (step.key === "revenue" && !inputs.revenue_streams.some((r) => (r.name || "").trim())) return setNoIncome(true);
    goNext();
  };
  const addContingency = (c) => {
    addCost("monthly", { ...emptyCost("monthly"), ...c, name: "Contingency (unexpected costs)", is_contingency: true });
    setInputs((s) => ({ ...s, contingency_asked: true }));
    setContingency(null);
  };
  const declineContingency = (proceed) => {
    const fromNext = contingency?.fromNext;
    setInputs((s) => ({ ...s, contingency_asked: true }));
    setContingency(null);
    if (proceed && fromNext) goNext();
  };
  const updateCost = (kind, i, patch) =>
    setInputs((s) => {
      const key = kind === "monthly" ? "recurring_costs" : "one_off_costs";
      return { ...s, [key]: s[key].map((c, k) => (k === i ? { ...c, ...patch } : c)) };
    });
  const removeCost = (kind, i) =>
    setInputs((s) => {
      const key = kind === "monthly" ? "recurring_costs" : "one_off_costs";
      return { ...s, [key]: s[key].filter((_, k) => k !== i) };
    });

  const buildForecastPayload = () => ({
    inputs: {
      business_name: inputs.business_name,
      // Iter40: always send today's YYYY-MM. Even if a stale draft in
      // localStorage carries an older start_month, we anchor to now so the
      // backend guardrail accepts the payload.
      start_month: todayStartMonth(),
      opening_cash: Number(inputs.opening_cash) || 0,
      revenue_streams: inputs.revenue_streams
        .filter((r) => r.name)
        .map((r) => ({
          id: r.id,
          name: r.name,
          price: Number(r.price) || 0,
          clients_per_month: r.offer_type === "other" ? 1 : Number(r.clients_per_month) || 0,
          seasonality:
            followsRhythm(r) && !(inputs.seasonality_excluded || []).includes(r.id)
              ? SEASONALITY_PRESETS[inputs.seasonality_preset] || null
              : null,
          monthly_amounts:
            r.offer_type === "other" && Array.isArray(r.monthly_amounts) ? r.monthly_amounts.map((n) => Number(n) || 0) : null,
          is_future: !!r.is_future,
          launch_month_index: Number(r.launch_month_index) || 0,
          offer_type: r.offer_type || "service",
          monthly_sales:
            Array.isArray(r.monthly_sales) && r.monthly_sales.length === 12
              ? r.monthly_sales.map((n) => Number(n) || 0)
              : null,
        })),
      recurring_costs: inputs.recurring_costs
        .filter((c) => c.name)
        .map((c) => {
          const amount = c.is_contingency ? contingencyAmount(inputs.recurring_costs, c) : Number(c.amount) || 0;
          const custom = c.frequency === "custom" && Array.isArray(c.monthly_amounts);
          return {
            id: c.id,
            name: c.name,
            amount,
            kind: "monthly",
            frequency: c.is_contingency ? "monthly" : c.frequency || "monthly",
            anchor_month_index: Number(c.anchor_month_index) || 0,
            months_active: c.frequency === "custom" && Array.isArray(c.months_active) ? c.months_active : null,
            monthly_amount_overrides: custom
              ? c.monthly_amounts.map((v, k) => ((c.months_active || []).includes(k) ? (v ?? amount) : null))
              : null,
            is_contingency: !!c.is_contingency,
            contingency_pct: c.is_contingency && c.contingency_mode === "pct" ? Number(c.contingency_pct) || 0 : null,
          };
        }),
      one_off_costs: inputs.one_off_costs
        .filter((c) => c.name && c.amount > 0)
        .map((c) => ({
          id: c.id,
          name: c.name,
          amount: Number(c.amount) || 0,
          kind: "one_off",
          month_index: Math.max(0, Math.min(11, Number(c.month_index) || 0)),
        })),
      personal_draw: Number(inputs.personal_draw) || 0,
      currency: "GBP",
      notes: inputs.seasonality_note,
      seasonality_preset: inputs.seasonality_preset || "steady",
    },
  });

  const finish = async () => {
    setBusy(true);
    try {
      // 1) Persist business_profile to the user record BEFORE saving the forecast
      //    so the LLM shared brain has it available on the very first insight call.
      try {
        await axios.patch(`${API}/auth/profile`, {
          business_name: inputs.business_name,
          business_profile: inputs.business_profile,
        });
      } catch {
        // Non-fatal — the user can update this later from Account.
      }

      // 2) Save the forecast (single straightforward path — no more shift-back).
      const payload = buildForecastPayload();
      await axios.post(`${API}/forecast`, payload);

      // 3) Save any goals entered during onboarding.
      for (const g of inputs.goals) {
        if (!g.title) continue;
        try {
          await axios.post(`${API}/goals`, {
            title: g.title,
            why: g.why || null,
            target_value: g.target_value ? Number(g.target_value) : null,
            target_month_index: (() => {
              if (g.kind === "launch") {
                const st = inputs.revenue_streams.find((r) => r.id === g.linked_stream_id);
                return st ? launchMonthIndex(st) : null;
              }
              return g.target_month_index === "" || g.target_month_index == null ? null : Number(g.target_month_index) - 1;
            })(),
            kind: g.kind || "revenue",
            metric: g.metric || null,
            linked_stream_id: g.kind === "launch" && g.linked_stream_id !== "__none" ? g.linked_stream_id || null : null,
            decision_cost_type: g.kind === "big_decision" ? g.decision_cost_type || "monthly" : null,
          });
        } catch {
          /* ignore individual goal failure */
        }
      }

      finishedRef.current = true;
      axios.delete(`${API}/onboarding/draft`).catch(() => {});
      toast.success("Your 12-month forecast is ready.");
      // Iter24: always land on /forecast (not dashboard, not actuals) so the
      // user sees the money map first — which is what they just built.
      nav("/forecast");
    } catch (err) {
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong saving your forecast.";
      const detailStr = typeof detail === "string" ? detail : JSON.stringify(detail);
      let msg = detailStr;
      if (status === 404) {
        msg =
          "We couldn't reach the forecast service (404). Try a hard refresh (Ctrl/Cmd + Shift + R). Your answers are saved.";
      } else if (status === 401) {
        msg = "Your session has expired. Please sign in again — your answers are saved.";
      }
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("Forecast save error:", { status, detail, err });
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="onboarding-loading">
        <p className="font-heading uppercase tracking-wide text-black/60">Loading your progress…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <NewMonthDialog
        open={!!newMonth}
        monthLabel={monthLabel(inputs.start_month, 0)}
        firstDayLabel={`1 ${monthLabel(inputs.start_month, 0).split(" ")[0]}`}
        dropped={newMonth?.dropped || []}
        onUpdate={() => {
          const k = newMonth.resumeKey;
          setNewMonth(null);
          startDetour("opening_cash", k);
        }}
        onSkip={() => setNewMonth(null)}
      />
      <NoIncomeDialog
        open={noIncome}
        onAdd={() => setNoIncome(false)}
        onCarryOn={() => {
          setNoIncome(false);
          goNext();
        }}
      />
      <ContingencyDialog
        open={!!contingency}
        costs={inputs.recurring_costs}
        onAdd={addContingency}
        onDecline={declineContingency}
      />
      <header className="border-b border-black/15 bg-white sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-28 gap-6">
          <Link to="/dashboard" className="flex items-center" data-testid="onboarding-logo">
            <img
              src={FORECASTLY_LOGO_LIGHT}
              alt="forecastly"
              className="w-[220px] h-auto object-contain"
            />
          </Link>
          <div className="hidden sm:flex items-center gap-2" data-testid="progress-dots">
            {STEPS.map((s, i) => (
              <div key={s.key} className={`progress-dot ${i < idx ? "done" : i === idx ? "active" : ""}`} />
            ))}
          </div>
          <Link
            to="/dashboard"
            onClick={async (e) => {
              e.preventDefault();
              await saveDraft();
              nav("/dashboard");
            }}
            className="text-sm font-semibold underline hidden sm:inline-flex items-center gap-1.5"
            data-testid="onboarding-return-dashboard"
          >
            <House size={14} weight="bold" /> Save & return to dashboard
          </Link>
        </div>
        <div className="h-1 bg-black/10">
          <div className="h-full bg-[#FFBD4A]" style={{ width: `${progress}%` }} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <AnimatePresence mode="wait">
          <motion.div
            key={step.key}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.3 }}
          >
            <div className="section-eyebrow" data-testid="step-counter">
              Step {String(idx + 1).padStart(2, "0")} of {STEPS.length}
            </div>
            <h1
              className="font-heading uppercase text-4xl md:text-5xl tracking-wide mt-4 leading-[1.05]"
              data-testid="step-title"
            >
              {step.intro ? `Hello ${user?.name?.split(" ")[0] || "friend"}` : step.title}
            </h1>
            {step.subtitle && <p className="mt-3 text-lg text-black/70">{step.subtitle}</p>}
            <StepVideo url={videos[step.key]} stepKey={step.key} intro={step.intro} />

            <div className="mt-10">
              {step.key === "welcome" && <WelcomeStep />}

              {step.key === "business" && (
                <BusinessStep
                  inputs={inputs}
                  setName={(v) => setInputs((s) => ({ ...s, business_name: v }))}
                  patchBiz={patchBiz}
                />
              )}

              {step.key === "opening_cash" && (
                <OpeningCashStep
                  value={inputs.opening_cash}
                  onChange={(n) => setInputs((s) => ({ ...s, opening_cash: n }))}
                  firstDay={`1 ${monthLabel(inputs.start_month, 0)}`}
                  lastDayPrev={(() => {
                    const [y, m] = inputs.start_month.split("-").map(Number);
                    return `${new Date(y, m - 1, 0).getDate()} ${LONG[(m + 10) % 12]}`;
                  })()}
                  highlight={!!detour}
                />
              )}

              {step.key === "revenue" && (
                <IncomeStep
                  streams={inputs.revenue_streams}
                  startIso={inputs.start_month}
                  vat={inputs.business_profile.vat_registered}
                  focusId={focusCardId}
                  makeItem={emptyOffer}
                  onAdd={(item) => setInputs((s) => ({ ...s, revenue_streams: [...s.revenue_streams, item] }))}
                  onPatch={updateOffer}
                  onRemove={removeOffer}
                  onDetour={(key, id) => {
                    setFocusCardId(id);
                    startDetour(key, "revenue");
                  }}
                />
              )}

              {step.key === "seasonality" && (
                <RhythmStep
                  inputs={inputs}
                  setPreset={(k) => setInputs((s) => ({ ...s, seasonality_preset: k }))}
                  setNote={(v) => setInputs((s) => ({ ...s, seasonality_note: v }))}
                  toggleStream={(id) =>
                    setInputs((s) => {
                      const ex = s.seasonality_excluded || [];
                      return { ...s, seasonality_excluded: ex.includes(id) ? ex.filter((x) => x !== id) : [...ex, id] };
                    })
                  }
                />
              )}

              {step.key === "recurring_costs" && (
                <RegularCostsStep
                  costs={inputs.recurring_costs}
                  startIso={inputs.start_month}
                  vat={inputs.business_profile.vat_registered}
                  makeItem={() => emptyCost("monthly")}
                  onAdd={(item) => addCost("monthly", item)}
                  onPatch={(i, p) => updateCost("monthly", i, p)}
                  onRemove={(i) => removeCost("monthly", i)}
                  contingencyAsked={!!inputs.contingency_asked}
                  onOpenContingency={() => setContingency({ fromNext: false })}
                />
              )}

              {step.key === "one_off" && (
                <OneOffCostsStep
                  costs={inputs.one_off_costs}
                  startIso={inputs.start_month}
                  vat={inputs.business_profile.vat_registered}
                  makeItem={() => emptyCost("one_off")}
                  onAdd={(item) => addCost("one_off", item)}
                  onPatch={(i, p) => updateCost("one_off", i, p)}
                  onRemove={(i) => removeCost("one_off", i)}
                />
              )}

              {step.key === "personal_draw" && (
                <PayingYourselfStep
                  value={inputs.personal_draw}
                  onChange={(n) => setInputs((s) => ({ ...s, personal_draw: n }))}
                  isLtd={inputs.business_profile.legal_entity === "ltd"}
                />
              )}

              {step.key === "goals" && (
                <GoalsStep
                  goals={inputs.goals}
                  streams={inputs.revenue_streams}
                  startIso={inputs.start_month}
                  makeItem={emptyGoal}
                  onAdd={(item) => setInputs((s) => ({ ...s, goals: [...s.goals, item] }))}
                  onPatch={updateGoal}
                  onRemove={removeGoal}
                  onEditStream={(id) => {
                    setFocusCardId(id);
                    startDetour("revenue", "goals");
                  }}
                  onAddStream={() => startDetour("revenue", "goals")}
                />
              )}

              {step.key === "nearly_there" && (
                <NearlyThereStep inputs={inputs} busy={busy} onFinish={finish} jumpTo={jumpTo} />
              )}
            </div>

            <div className="mt-12 flex items-center justify-between">
              {step.key === "welcome" ? (
                <span />
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={goBack}
                  disabled={idx <= 0}
                  data-testid="onboarding-back-btn"
                >
                  <ArrowLeft size={18} weight="bold" /> Back
                </button>
              )}
              {step.key !== "nearly_there" || detour ? (
                <div className="flex flex-col items-end gap-2">
                  {nextDisabled && step.key === "business" && (
                    <span className="text-xs text-red-700 font-semibold" data-testid="business-next-hint">
                      Please fill in Business name, Legal entity and VAT first.
                    </span>
                  )}
                  <div className="flex flex-wrap items-center justify-end gap-3">
                    {detour && step.key !== "nearly_there" && (
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => !nextDisabled && handleNext()}
                        disabled={nextDisabled}
                        data-testid="onboarding-next-btn"
                      >
                        Next <ArrowRight size={18} weight="bold" />
                      </button>
                    )}
                    <button
                      type="button"
                      className={`btn-primary ${nextDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                      onClick={() => {
                        if (nextDisabled) return;
                        if (detour) endDetour();
                        else handleNext();
                      }}
                      disabled={nextDisabled}
                      data-testid={detour ? "onboarding-detour-return-btn" : "onboarding-next-btn"}
                    >
                      {detour ? `Back to ${detourLabel(detour.returnKey)}` : step.key === "welcome" ? "Let's begin" : "Next"}{" "}
                      <ArrowRight size={18} weight="bold" />
                    </button>
                  </div>
                </div>
              ) : (
                <span />
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
